import { supabase } from './supabaseClient';

export interface ClientSearchResult {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  vat_number?: string;
}

export interface ClientSearchOptions {
  limit?: number;
  searchFields?: ('full_name' | 'email' | 'phone' | 'address' | 'city')[];
}

/**
 * Service pour la recherche intelligente de clients
 */
export const clientSearchService = {
  /**
   * Rechercher des clients avec auto-complétion
   */
  async searchClients(
    query: string,
    options: ClientSearchOptions = {}
  ): Promise<ClientSearchResult[]> {
    try {
      const { limit = 3, searchFields = ['full_name', 'email', 'phone', 'address', 'city'] } =
        options;

      if (!query || query.trim().length < 1) {
        return [];
      }

      const searchTerm = query.trim();

      // Construire la requête de recherche dynamiquement
      const searchConditions = searchFields
        .map(field => `${field}.ilike.%${searchTerm}%`)
        .join(',');

      const { data, error } = await supabase
        .from('clients')
        .select(
          `
          id,
          full_name,
          email,
          phone,
          address,
          city,
          postal_code,
          country,
          vat_number
        `
        )
        .or(searchConditions)
        .order('full_name', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Erreur lors de la recherche de clients:', error);
        throw new Error(`Erreur de recherche: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Erreur dans searchClients:', error);
      throw error;
    }
  },

  /**
   * Obtenir un client par ID
   */
  async getClientById(id: string): Promise<ClientSearchResult | null> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(
          `
          id,
          full_name,
          email,
          phone,
          address,
          city,
          postal_code,
          country,
          vat_number
        `
        )
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erreur lors de la récupération du client:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans getClientById:', error);
      return null;
    }
  },
};
